import {
  Box,
  Button,
  FormHelperText,
  Paper,
  TextField,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { get } from 'lodash';
import { ReactElement } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { AdditionalButton } from '@app/components/Shared/Button/AdditionalButton';
import { DecimalPointSeparate } from '@app/components/Shared/Inputs/DecimalPointSeparate';
import { ImageUpload } from '@app/components/Shared/Inputs/ImageUpload';
import { NumericTextField } from '@app/components/Shared/Inputs/NumericTextField';
import { PROPERTY_VARIANT_FLOOR_PLAN_CATEGORY_VALUES as CATEGORY_VALUES } from '@app/utils/constants';
import { getResizeImageURL } from '@app/utils/fileUpload';

export function FloorPlanFormSection(): ReactElement {
  const {
    clearErrors,
    control,
    formState: { errors },
    setError,
    setValue,
    watch,
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'floorPlans',
  });

  return (
    <Stack spacing={3}>
      <Typography variant="h5">間取り</Typography>
      {fields.map((field, index) => (
        <Stack key={field.id} spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="flexEnd">
            <Typography variant="h6">{index + 1}</Typography>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => remove(index)}
              sx={{ height: 32 }}
            >
              <Typography variant="body3">クリア</Typography>
            </Button>
          </Stack>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack direction="row" spacing={3} useFlexGap>
              <Box flex={1}>
                <ImageUpload
                  fileInfo={watch(`floorPlans.${index}.url`)}
                  onFileUpload={async (urls) => {
                    const resizeUrl = await getResizeImageURL(
                      urls[0],
                      undefined,
                      undefined,
                      3 / 2
                    );
                    clearErrors(`floorPlans.${index}`);
                    setValue(`floorPlans.${index}.imageId`, '');
                    setValue(`floorPlans.${index}.url`, resizeUrl);
                  }}
                  onError={(e) =>
                    setError(`floorPlans.${index}`, { message: e.message })
                  }
                />
                {Array.from(
                  new Set([get(errors, `floorPlans.${index}.message`, '')])
                )
                  .filter((m) => !!m)
                  .map((message, index) => (
                    <FormHelperText key={index} error>
                      {message as string}
                    </FormHelperText>
                  ))}
              </Box>
              <Stack spacing={1.5} flex={1}>
                <FormControl fullWidth>
                  <FormLabel focused={false}>画像カテゴリー</FormLabel>
                  <Controller
                    name={`floorPlans.${index}.customFields.category`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Select {...field} error={!!error} displayEmpty>
                        {Object.keys(CATEGORY_VALUES)
                          .sort((a, b) => a.localeCompare(b))
                          .map((key, index) => (
                            <MenuItem key={index} value={key}>
                              {CATEGORY_VALUES[key]}
                            </MenuItem>
                          ))}
                      </Select>
                    )}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <FormLabel focused={false}>説明タイトル</FormLabel>
                  <Controller
                    name={`floorPlans.${index}.title`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <FormLabel focused={false}>間取り</FormLabel>
                  <Controller
                    name={`floorPlans.${index}.description`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </FormControl>
                <Stack>
                  <FormLabel focused={false}>土地面積</FormLabel>
                  <DecimalPointSeparate
                    field1stName={`floorPlans.${index}.customFields.landArea.0`}
                    field2ndName={`floorPlans.${index}.customFields.landArea.1`}
                    endAdornment="㎡"
                  />
                </Stack>
                <FormControl fullWidth>
                  <FormLabel focused={false}>建物面積</FormLabel>
                  <DecimalPointSeparate
                    field1stName={`floorPlans.${index}.customFields.buildingArea.0`}
                    field2ndName={`floorPlans.${index}.customFields.buildingArea.1`}
                    endAdornment="㎡"
                  />
                </FormControl>
                <FormControl fullWidth>
                  <FormLabel focused={false}>販売価格</FormLabel>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Controller
                      name={`floorPlans.${index}.customFields.price`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <NumericTextField
                          {...field}
                          error={!!error}
                          helperText={error?.message}
                          inputMode="decimal"
                        />
                      )}
                    />
                    <Typography>万円</Typography>
                  </Stack>
                </FormControl>
                <FormControl fullWidth>
                  <FormLabel focused={false}>間取りコメント</FormLabel>
                  <Controller
                    name={`floorPlans.${index}.customFields.comment`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </FormControl>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      ))}
      <AdditionalButton
        label="間取りの追加"
        disabled={fields.length >= 10}
        fullWidth
        onClick={() =>
          append({
            customFields: {
              buildingArea: ['', ''],
              category: '',
              comment: '',
              landArea: ['', ''],
              price: '',
            },
            description: '',
            id: '',
            imageId: '',
            title: '',
            url: '',
          })
        }
      />
    </Stack>
  );
}
